import {
  MAX_ITEM_QUANTITY,
  PartsOrderCartItem,
  availabilityAt,
  displayPrice,
  formatCurrency,
} from '../../services/partsOrderApi';
import { displayPartNumber } from '../../utils/displayPartNumber';

interface CartLinesProps {
  items: PartsOrderCartItem[];
  onUpdateQuantity: (partNumber: string, quantity: number) => void;
  onRemove: (partNumber: string) => void;
  disabled?: boolean;
  // When set (review step, pickup location chosen), badges show that
  // branch's real status instead of org-wide stock.
  pickupLocationId?: number | null;
}

const BADGE: Record<string, { label: string; variant: string }> = {
  in_stock: { label: 'In stock', variant: 'in-stock' },
  transfer_needed: { label: 'Transfer needed', variant: 'transfer' },
  vendor_order_needed: { label: 'Vendor order', variant: 'vendor' },
};

// Cart line items with qty stepper + remove, shared by the shop cart bar and
// the review step. cart-panel__* markup borrowed from the agent's CartPanel.
function CartLines({
  items,
  onUpdateQuantity,
  onRemove,
  disabled = false,
  pickupLocationId = null,
}: CartLinesProps) {
  return (
    <ul className="cart-panel__items">
      {items.map((item) => {
        const price = displayPrice(item);
        const lineTotal = price != null ? price * item.quantity : null;
        const status =
          pickupLocationId != null ? availabilityAt(item, pickupLocationId) : item.availability_status;
        const badge = BADGE[status] ?? BADGE.vendor_order_needed;
        const badgeLabel =
          pickupLocationId != null && status === 'in_stock' ? 'In stock here' : badge.label;
        return (
          <li key={item.part_number} className="cart-panel__item">
            <div className="cart-panel__item-main">
              <div className="cart-panel__item-part">{displayPartNumber(item.part_number)}</div>
              {item.description && (
                <div className="cart-panel__item-desc">{item.description}</div>
              )}
              <div className="cart-panel__item-meta">
                <span className="parts-order-qty">
                  <button
                    type="button"
                    onClick={() => onUpdateQuantity(item.part_number, item.quantity - 1)}
                    disabled={disabled || item.quantity <= 1}
                    aria-label={`Decrease ${item.part_number} quantity`}
                  >
                    −
                  </button>
                  <span>{item.quantity}</span>
                  <button
                    type="button"
                    onClick={() => onUpdateQuantity(item.part_number, item.quantity + 1)}
                    disabled={disabled || item.quantity >= MAX_ITEM_QUANTITY}
                    aria-label={`Increase ${item.part_number} quantity`}
                  >
                    +
                  </button>
                </span>
                {price != null && (
                  <span className="cart-panel__item-price">{formatCurrency(price)} ea</span>
                )}
                <span className={`cart-panel__availability cart-panel__availability--${badge.variant}`}>
                  {badgeLabel}
                </span>
              </div>
            </div>
            <div className="cart-panel__item-right">
              {lineTotal != null && (
                <div className="cart-panel__item-total">{formatCurrency(lineTotal)}</div>
              )}
              <button
                type="button"
                className="cart-panel__remove"
                onClick={() => onRemove(item.part_number)}
                disabled={disabled}
                aria-label={`Remove ${item.part_number}`}
                title={`Remove ${item.part_number}`}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export default CartLines;
